/*
  SALZ – Zubehör-Popup
  Öffnet nach "In den Warenkorb" auf einer Gewächshaus-Seite einen Dialog mit dem passenden Zubehör
  (Abschnitt sections/sz-zubehoer-popup.liquid). Länge und Farbe werden passend zum gewählten Haus
  vorausgewählt, längere Längen als das Haus werden nicht angeboten. Die Hauslänge kommt aus dem
  Auswahlfeld "Länge" oder aus dem Feld "Größe" (z. B. "2,36 × 3,09 m"). Hat das Haus ein Feld "Höhe",
  wird bei Zubehör mit Höhenwahl (Türen) die passende Höhe vorausgewählt.
*/
if (!customElements.get('sz-zubehoer-popup')) {
  customElements.define(
    'sz-zubehoer-popup',
    class SzZubehoerPopup extends HTMLElement {
      connectedCallback() {
        this.dialog = this.querySelector('dialog');
        if (!this.dialog || typeof this.dialog.showModal !== 'function') return;

        this.house = this.readJson(this.querySelector('[data-sz-house]')) || { options: [], variants: [] };
        this.items = Array.from(this.querySelectorAll('[data-sz-item]')).map((el) => this.setupItem(el));

        this.onDialogClick = this.onDialogClick.bind(this);
        this.onDialogClose = this.onDialogClose.bind(this);
        this.onSectionSelect = this.onSectionSelect.bind(this);
        this.onSectionDeselect = this.onSectionDeselect.bind(this);

        this.dialog.addEventListener('click', this.onDialogClick);
        this.dialog.addEventListener('close', this.onDialogClose);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
        }

        if (window.Shopify && window.Shopify.designMode) {
          document.addEventListener('shopify:section:select', this.onSectionSelect);
          document.addEventListener('shopify:section:deselect', this.onSectionDeselect);
        }
      }

      disconnectedCallback() {
        if (this.unsubscribe) this.unsubscribe();
        document.removeEventListener('shopify:section:select', this.onSectionSelect);
        document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
        document.body.classList.remove('overflow-hidden');
      }

      /* ---------- Aufbau ---------- */

      readJson(node) {
        if (!node) return null;
        try {
          return JSON.parse(node.textContent);
        } catch (error) {
          console.error('sz-zubehoer-popup: Daten konnten nicht gelesen werden', error);
          return null;
        }
      }

      setupItem(el) {
        const item = {
          el,
          title: el.dataset.title || '',
          variants: this.readJson(el.querySelector('[data-sz-variants]')) || [],
          selects: Array.from(el.querySelectorAll('select[data-sz-option]')),
          quantity: el.querySelector('[data-sz-quantity]'),
          price: el.querySelector('[data-sz-price]'),
          button: el.querySelector('[data-sz-add]'),
          status: el.querySelector('[data-sz-status]'),
          variant: null,
          busy: false,
        };

        // Alle Optionswerte merken, damit die Längenliste je nach Haus neu aufgebaut werden kann.
        item.selects.forEach((select) => {
          select.szAllValues = Array.from(select.options).map((option) => option.value);
          select.addEventListener('change', () => this.updateItem(item));
        });

        if (item.button) item.button.addEventListener('click', () => this.addItem(item));
        return item;
      }

      /* ---------- Hilfen ---------- */

      isLength(name) {
        return /l(ä|ae|a)nge/i.test(name || '');
      }

      isColor(name) {
        return /farbe/i.test(name || '');
      }

      isSize(name) {
        return /gr(ö|oe|o)(ß|ss)e/i.test(name || '');
      }

      isHeight(name) {
        return /h(ö|oe|o)he/i.test(name || '');
      }

      isRaised(value) {
        return /erh(ö|oe|o)ht|\+\s*20/i.test(String(value == null ? '' : value));
      }

      // "2,36 × 3,09 m" -> 3.09
      lengthFromSize(value) {
        const parts = String(value == null ? '' : value).split('×');
        return parts.length === 2 ? this.parseNumber(parts[1]) : null;
      }

      isDimension(name) {
        return /(breite|l(ä|ae|a)nge|tiefe|h(ö|oe|o)he)/i.test(name || '');
      }

      parseNumber(value) {
        const match = String(value == null ? '' : value)
          .replace(',', '.')
          .match(/\d+(\.\d+)?/);
        return match ? parseFloat(match[0]) : null;
      }

      normalize(value) {
        return String(value == null ? '' : value)
          .toLowerCase()
          .replace(/\s+/g, ' ')
          .trim();
      }

      ralCode(value) {
        const match = String(value == null ? '' : value).match(/ral\s*(\d{4})/i);
        return match ? match[1] : null;
      }

      formatOption(name, value) {
        const text = String(value == null ? '' : value).trim();
        if (this.isDimension(name) && /^\d+([.,]\d+)?$/.test(text)) return `${text.replace('.', ',')} m`;
        return text;
      }

      /* ---------- Vorbereitung für das gewählte Haus ---------- */

      houseSelection(houseVariant) {
        const selection = { length: null, color: null, raised: null };
        let sizeLength = null;
        (this.house.options || []).forEach((name, index) => {
          const value = houseVariant && houseVariant.o ? houseVariant.o[index] : null;
          if (value == null) return;
          if (selection.length === null && this.isLength(name)) selection.length = this.parseNumber(value);
          if (sizeLength === null && this.isSize(name)) sizeLength = this.lengthFromSize(value);
          if (selection.color === null && this.isColor(name)) selection.color = value;
          if (selection.raised === null && this.isHeight(name)) selection.raised = this.isRaised(value);
        });
        if (selection.length === null) selection.length = sizeLength;
        return selection;
      }

      prepare(houseVariant, cartData) {
        const selection = this.houseSelection(houseVariant);
        let visible = 0;

        this.items.forEach((item) => {
          let usable = true;

          item.selects.forEach((select) => {
            const name = select.dataset.szOptionName;

            if (this.isLength(name)) {
              const allowed = select.szAllValues.filter((value) => {
                if (selection.length === null) return true;
                const number = this.parseNumber(value);
                return number === null || number <= selection.length + 0.005;
              });
              this.fillSelect(select, allowed);
              if (!allowed.length) {
                usable = false;
                return;
              }
              // Passende Länge = die größte, die noch ins Haus passt.
              let best = allowed[0];
              if (selection.length !== null) {
                allowed.forEach((value) => {
                  const number = this.parseNumber(value);
                  if (number !== null && number >= (this.parseNumber(best) || 0)) best = value;
                });
              }
              select.value = best;
            } else {
              this.fillSelect(select, select.szAllValues);
              if (this.isColor(name) && selection.color !== null) {
                const wanted = this.normalize(selection.color);
                const ral = this.ralCode(selection.color);
                const exact = select.szAllValues.find((value) => this.normalize(value) === wanted);
                const byRal = ral ? select.szAllValues.find((value) => this.ralCode(value) === ral) : null;
                select.value = exact || byRal || select.szAllValues[0];
              } else if (this.isHeight(name) && selection.raised !== null) {
                const height = select.szAllValues.find((value) => this.isRaised(value) === selection.raised);
                select.value = height || select.szAllValues[0];
              } else {
                select.value = select.szAllValues[0];
              }
            }
          });

          if (item.quantity) item.quantity.value = 1;
          this.setStatus(item, '', null);
          item.el.hidden = !usable;
          if (usable) {
            visible += 1;
            this.updateItem(item);
          }
        });

        this.fillAdded(houseVariant, cartData);
        return visible;
      }

      fillSelect(select, values) {
        const current = Array.from(select.options).map((option) => option.value);
        if (current.length === values.length && current.every((value, index) => value === values[index])) return;
        select.textContent = '';
        values.forEach((value) => {
          const option = document.createElement('option');
          option.value = value;
          option.textContent = value;
          select.appendChild(option);
        });
      }

      fillAdded(houseVariant, cartData) {
        const title = this.querySelector('[data-sz-added-title]');
        const options = this.querySelector('[data-sz-added-options]');
        const image = this.querySelector('[data-sz-added-image]');

        if (title) title.textContent = (cartData && cartData.product_title) || this.house.title || '';

        if (options) {
          const parts = [];
          (this.house.options || []).forEach((name, index) => {
            const value = houseVariant && houseVariant.o ? houseVariant.o[index] : null;
            if (value == null || value === 'Default Title') return;
            parts.push(this.isColor(name) ? this.formatOption(name, value) : `${name} ${this.formatOption(name, value)}`);
          });
          const quantity = cartData && Number(cartData.quantity) > 1 ? `${cartData.quantity} × ` : '';
          options.textContent = quantity + parts.join(' · ');
        }

        const source = cartData && ((cartData.featured_image && cartData.featured_image.url) || cartData.image);
        if (image && source) {
          const url = String(source);
          image.src = url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=160';
        }
      }

      /* ---------- Artikelzeile ---------- */

      findVariant(item) {
        if (!item.selects.length) return item.variants[0] || null;
        const chosen = item.selects.map((select) => select.value);
        return (
          item.variants.find((variant) =>
            chosen.every((value, index) => variant.o && variant.o[Number(item.selects[index].dataset.szOption)] === value)
          ) || null
        );
      }

      updateItem(item) {
        item.variant = this.findVariant(item);
        const available = !!(item.variant && item.variant.a);
        if (item.price) item.price.textContent = item.variant ? item.variant.p : '';
        if (item.button && !item.busy) {
          item.button.disabled = !available;
          item.button.textContent = available ? this.dataset.labelAdd : this.dataset.labelUnavailable;
        }
      }

      setStatus(item, text, kind) {
        if (!item.status) return;
        item.status.textContent = text;
        item.status.classList.toggle('sz-zp__status--ok', kind === 'ok');
        item.status.classList.toggle('sz-zp__status--error', kind === 'error');
      }

      variantLabel(item) {
        const values = item.selects.map((select) => select.value).filter(Boolean);
        return values.length ? `${item.title} (${values.join(', ')})` : item.title;
      }

      addItem(item) {
        if (item.busy || !item.variant || !item.variant.a) return;

        let quantity = parseInt(item.quantity ? item.quantity.value : '1', 10);
        if (!Number.isFinite(quantity) || quantity < 1) quantity = 1;
        if (quantity > 99) quantity = 99;
        if (item.quantity) item.quantity.value = quantity;

        const label = this.variantLabel(item);
        item.busy = true;
        item.button.disabled = true;
        item.button.textContent = this.dataset.labelAdding;
        this.setStatus(item, '', null);

        fetch(`${this.dataset.cartAddUrl}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            items: [{ id: item.variant.id, quantity }],
            sections: ['cart-icon-bubble'],
            sections_url: window.location.pathname,
          }),
        })
          .then((response) => response.json())
          .then((data) => {
            if (data.status) {
              this.setStatus(item, data.description || data.message || this.dataset.textError, 'error');
              return;
            }
            this.renderCartBubble(data.sections);
            this.setStatus(item, `✓ ${quantity} × ${label} ${this.dataset.textInCart}`, 'ok');
            item.button.textContent = `✓ ${this.dataset.labelAdded}`;
            item.justAdded = true;
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'sz-zubehoer-popup', cartData: data });
            }
          })
          .catch((error) => {
            console.error(error);
            this.setStatus(item, this.dataset.textError, 'error');
          })
          .finally(() => {
            item.busy = false;
            if (item.justAdded) {
              item.justAdded = false;
              item.button.disabled = false;
              window.setTimeout(() => {
                if (!item.busy) this.updateItem(item);
              }, 2000);
            } else {
              this.updateItem(item);
            }
          });
      }

      renderCartBubble(sections) {
        const html = sections && sections['cart-icon-bubble'];
        const target = document.getElementById('cart-icon-bubble');
        if (!html || !target) return;
        const source = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
        if (source) target.innerHTML = source.innerHTML;
      }

      /* ---------- Öffnen und Schließen ---------- */

      onCartUpdate(event) {
        if (!event || event.source !== 'product-form') return;
        const variantId = Number(event.productVariantId);
        const houseVariant = (this.house.variants || []).find((variant) => Number(variant.id) === variantId);
        if (!houseVariant) return;

        const visible = this.prepare(houseVariant, event.cartData);
        if (!visible) return;

        // Der Warenkorb-Hinweis des Themes würde gleichzeitig aufgehen. Sein Inhalt (Warenkorb-Symbol)
        // wird weiterhin aktualisiert, nur das Aufklappen wird dieses eine Mal übersprungen.
        this.skipThemeNotice();

        Promise.resolve().then(() => this.open());
      }

      skipThemeNotice() {
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (!cart || typeof cart.open !== 'function') return;
        const restore = () => {
          if (Object.prototype.hasOwnProperty.call(cart, 'open')) delete cart.open;
        };
        cart.open = () => {
          restore();
        };
        window.setTimeout(restore, 1500);
      }

      closeThemeNotice() {
        const notice = document.getElementById('cart-notification');
        const cart = document.querySelector('cart-notification');
        if (notice && cart && notice.classList.contains('active') && typeof cart.close === 'function') cart.close();
      }

      open() {
        this.closeThemeNotice();
        if (this.dialog.open) return;
        this.dialog.showModal();
        document.body.classList.add('overflow-hidden');
        const scroll = this.querySelector('.sz-zp__scroll');
        if (scroll) scroll.scrollTop = 0;
      }

      close() {
        if (this.dialog.open) this.dialog.close();
      }

      onDialogClick(event) {
        // Klick auf den abgedunkelten Hintergrund oder auf eine Schließen-Schaltfläche.
        if (event.target === this.dialog || event.target.closest('[data-sz-close]')) this.close();
      }

      onDialogClose() {
        document.body.classList.remove('overflow-hidden');
      }

      /* ---------- Vorschau im Theme-Editor ---------- */

      onSectionSelect(event) {
        if (!event.detail || event.detail.sectionId !== this.dataset.sectionId) return;
        const houseVariant = (this.house.variants || [])[0];
        if (!houseVariant) return;
        if (this.prepare(houseVariant, null)) this.open();
      }

      onSectionDeselect(event) {
        if (!event.detail || event.detail.sectionId !== this.dataset.sectionId) return;
        this.close();
      }
    }
  );
}
